import { useCallback, useMemo } from 'react'
import { batch } from '@preact/signals-react'
import { useNavigate } from 'react-router-dom'
import { postSubmit } from '@ui-seeker/utils'

import { isOfficeGroup, isSiteGroup } from '@signals'
import { slaveId, userData } from '@signals/user'
import { BUSINESS_LOGISTICS_ROUTES } from '@constants/routes'
import { BACKEND_APIS } from '@constants'
import useDashboardOptions from 'Pages/BusinessLogistics/Dashboards/@hooks/useDashboardOptions'
import { businessLogisticsChildren } from 'Pages/BusinessLogistics/@services/menu'
import { clearedForm, goalTarget, prefillGoal, writeGoalsOpen } from 'Pages/BusinessLogistics/BI/Page/@services/goals'
import { BIActiveGoal, BIGoalsOpen, BIWhyOpen, setFormData } from 'Pages/BusinessLogistics/BI/Page/@signals'


export const goalsUid = () => slaveId.value || userData.value?.uid

/** CA 17: skips, re-opens and exits to a dashboard, one row each (webback `bi-goal-event`); never in the user's way */
export const trackGoalEvent = body => {
	postSubmit(`${BACKEND_APIS.DASHBOARD}/bi-goal-event`, body).catch(() => {})
}

/** "Go straight to the form": closed, and remembered */
export const skipGoals = () => {
	batch(() => {
		BIGoalsOpen.value = false
		writeGoalsOpen(localStorage, goalsUid(), false)
	})
	trackGoalEvent({ event: 'skip' })
}

/** "Start from a question" (remembered, counted) or "Other question" (this visit only) */
export const openGoals = (remember = true) => {
	batch(() => {
		BIGoalsOpen.value = true
		if (remember) writeGoalsOpen(localStorage, goalsUid(), true)
	})
	if (remember) trackGoalEvent({ event: 'reopen' })
}

/**
 * The questions of Advanced (BI) (CU-869fmk5ge piece 1): where each one leads for this user (the menu they see),
 * what it fills (with the offices, lengths, currencies and last query of `useDashboardOptions`), and the open/closed
 * memory of the cards.
 */
export default function useGoals() {
	const navigate = useNavigate()
	const options = useDashboardOptions()
	const entries = useMemo(() => businessLogisticsChildren(userData.value?.featuresV2 || {}, BUSINESS_LOGISTICS_ROUTES), [userData.value?.featuresV2])
	const ctx = { today: new Date(), lastOffice: options.lastOffice, lastCurrency: options.lastCurrency, offices: options.offices, currencies: options.currencies }

	const target = useCallback(goal => goalTarget(goal, entries), [entries])

	const pick = useCallback(goal => {
		const to = goalTarget(goal, entries)
		if (to.kind === 'dashboard') {
			trackGoalEvent({ event: 'dashboard', goal: goal.id, target: to.key })
			navigate(to.path)
			return
		}
		const filled = prefillGoal(goal, ctx)
		batch(() => {
			isOfficeGroup.value = false
			isSiteGroup.value = false
			setFormData({ ...filled.form })
			BIActiveGoal.value = { goal, ...filled }
			BIGoalsOpen.value = false
			// unfolded until the first search, then folded to its header (UX D2)
			BIWhyOpen.value = false
		})
	}, [entries, ctx.lastOffice, ctx.lastCurrency, options.offices, options.currencies])

	/** "Clear form": the form as the page opens it, no question */
	const clear = useCallback(() => batch(() => {
		isOfficeGroup.value = false
		isSiteGroup.value = false
		setFormData(clearedForm(ctx))
		BIActiveGoal.value = null
	}), [options.currencies])

	return { loading: options.loading, target, pick, skip: skipGoals, open: openGoals, clear }
}
